import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag, Eye } from 'lucide-react';

const BlogCard = ({ post, index }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: index * 0.1,
        ease: 'easeOut',
      },
    },
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -8 }}
      className="group"
    >
      <Link to={`/post/${post.id}`} className="block">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300">
          {/* Image */}
          {post.imageUrl && (
            <div className="relative h-64 overflow-hidden bg-gray-100">
              <motion.img
                src={post.imageUrl}
                alt={post.title}
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.6 }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {/* Category */}
            {post.category && (
              <div className="flex items-center mb-3">
                <Tag size={14} className="mr-2 text-gray-600" />
                <span className="text-xs font-body font-medium uppercase tracking-wider text-gray-600">
                  {post.category}
                </span>
              </div>
            )}

            {/* Title */}
            <h3 className="heading-4 mb-3 group-hover:text-gray-700 transition-colors">
              {post.title}
            </h3>

            {/* Summary */}
            <p className="text-gray-600 mb-4 line-clamp-3 font-body">
              {post.summary || post.content?.substring(0, 150) + '...'}
            </p>

            {/* Meta */}
            <div className="flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <Calendar size={14} className="mr-1" />
                  <span className="font-body text-xs">
                    {formatDate(post.createdAt)}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock size={14} className="mr-1" />
                  <span className="font-body text-xs">{post.readTime || '5'} min read</span>
                </div>
                <div className="flex items-center">
                  <Eye size={14} className="mr-1" />
                  <span className="font-body text-xs">{post.views || 0}</span>
                </div>
              </div>
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {post.tags.slice(0, 3).map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-black text-white text-xs font-body rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default BlogCard;
